import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ElementCategory } from '@scoutaround/script-breakdown-shared';

import { parsePlanView, type PlanNavigate, type PlanView } from '../production/address.js';

/**
 * WHERE THE SCENE BREAKDOWN WORKSPACE IS LOOKING, AS AN ADDRESS.
 *
 * `/elements?scene=&filter=&q=&cat=&ie=&tod=&char=&sort=&item=&view=&req=`. The scene open, the
 * list's filters and the item in view are things somebody sends a colleague ("scene 41, the
 * weapons"), so all of them live in the URL wherever a router is mounted, and are written in ONE
 * navigation per change. `view` and `req` are the production plan's own (`?view=breakdown|
 * execution|resources|plan`, `?req=`), kept exactly as the plan's links write them.
 *
 * Without a router (a render test, an embed) the same state lives in the component.
 */
export type ProgressFilter = 'ALL' | 'TO_REVIEW' | 'NEEDS_RE_REVIEW' | 'REVIEWED';
export type SceneSort = 'SCRIPT' | 'LOCATION' | 'STATUS';

export interface ElementsAddress {
  readonly scene: string | null;
  readonly filter: ProgressFilter;
  readonly q: string;
  /** Scenes with at least one item in this category. */
  readonly cat: ElementCategory | null;
  /** Interior / exterior, as the heading states it. */
  readonly ie: string | null;
  /** Time of day, as the heading states it. */
  readonly tod: string | null;
  /** Scenes a character appears in (the index's `castNames`). */
  readonly char: string | null;
  readonly sort: SceneSort;
  /** The item in view on the right, by its key (`pe:…` / `el:…`). */
  readonly item: string | null;
  readonly view: PlanView;
  readonly req: string | null;
}

export type ElementsAddressPatch = Partial<ElementsAddress>;

export type ElementsNavigate = (patch: ElementsAddressPatch, options?: { readonly push?: boolean }) => void;

export const DEFAULT_ELEMENTS_ADDRESS: ElementsAddress = {
  scene: null,
  filter: 'ALL',
  q: '',
  cat: null,
  ie: null,
  tod: null,
  char: null,
  sort: 'SCRIPT',
  item: null,
  view: 'breakdown',
  req: null,
};

const FILTERS: readonly ProgressFilter[] = ['ALL', 'TO_REVIEW', 'NEEDS_RE_REVIEW', 'REVIEWED'];
const SORTS: readonly SceneSort[] = ['SCRIPT', 'LOCATION', 'STATUS'];

/** An id from the address: a ULID, a fixture id or an item key; anything else is ignored. */
function parseId(value: string | null): string | null {
  return value !== null && /^[A-Za-z0-9_:.-]{1,80}$/.test(value) ? value : null;
}

/** Free text from the address, capped: a heading, a character, a time of day. */
function parseText(value: string | null): string | null {
  if (value === null) return null;
  const trimmed = value.trim();

  return trimmed === '' ? null : trimmed.slice(0, 120);
}

export function readElementsAddress(params: URLSearchParams): ElementsAddress {
  const category = ElementCategory.safeParse(params.get('cat'));

  return {
    scene: parseId(params.get('scene')),
    filter: FILTERS.find((f) => f === params.get('filter')) ?? 'ALL',
    q: (params.get('q') ?? '').slice(0, 120),
    cat: category.success ? category.data : null,
    ie: parseText(params.get('ie')),
    tod: parseText(params.get('tod')),
    char: parseText(params.get('char')),
    sort: SORTS.find((s) => s === params.get('sort')) ?? 'SCRIPT',
    item: parseId(params.get('item')),
    view: parsePlanView(params.get('view')),
    req: parseId(params.get('req')),
  };
}

/**
 * The address with a patch applied. Every other parameter (`?discuss=`, `?thread=` …) is kept;
 * a value at its default leaves the URL, except `view`, which the plan's links write explicitly.
 */
export function writeElementsAddress(params: URLSearchParams, patch: ElementsAddressPatch): URLSearchParams {
  const next = new URLSearchParams(params);
  const put = (key: string, value: string | null, fallback: string | null = null) => {
    if (value === null || value === '' || value === fallback) next.delete(key);
    else next.set(key, value);
  };

  if (patch.scene !== undefined) put('scene', patch.scene);
  if (patch.filter !== undefined) put('filter', patch.filter, 'ALL');
  if (patch.q !== undefined) put('q', patch.q);
  if (patch.cat !== undefined) put('cat', patch.cat);
  if (patch.ie !== undefined) put('ie', patch.ie);
  if (patch.tod !== undefined) put('tod', patch.tod);
  if (patch.char !== undefined) put('char', patch.char);
  if (patch.sort !== undefined) put('sort', patch.sort, 'SCRIPT');
  if (patch.item !== undefined) put('item', patch.item);
  if (patch.view !== undefined) next.set('view', patch.view);
  if (patch.req !== undefined) put('req', patch.req);

  return next;
}

/** The workspace's address, read and written through the router — one write per change. */
export function useRoutedElementsAddress(): [ElementsAddress, ElementsNavigate] {
  const [params, setParams] = useSearchParams();
  const address = useMemo(() => readElementsAddress(params), [params]);
  const navigate = useCallback<ElementsNavigate>(
    (patch, options) => {
      setParams((current) => writeElementsAddress(current, patch), { replace: options?.push !== true });
    },
    [setParams],
  );

  return [address, navigate];
}

/** The same address held by the component, for a screen rendered without a router. */
export function useLocalElementsAddress(initial: ElementsAddressPatch = {}): [ElementsAddress, ElementsNavigate] {
  const [address, setAddress] = useState<ElementsAddress>({ ...DEFAULT_ELEMENTS_ADDRESS, ...initial });
  const navigate = useCallback<ElementsNavigate>((patch) => {
    setAddress((was) => ({ ...was, ...patch }));
  }, []);

  return [address, navigate];
}

/** The production plan's navigation (`view`, `scene`, `req`), written through this address. */
export function planNavigateFrom(navigate: ElementsNavigate): PlanNavigate {
  return (patch, options) => {
    navigate(
      {
        ...(patch.view === undefined ? {} : { view: patch.view }),
        ...(patch.scene === undefined ? {} : { scene: patch.scene }),
        ...(patch.req === undefined ? {} : { req: patch.req }),
      },
      options,
    );
  };
}
